import React, { useEffect, useState } from "react";
import { Button, Container, Form } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { Link, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { setShowLoginModal } from "../../store/slices/userSlice";
import {
	createBlogComment,
	deleteBlogComment,
	getBlogComments,
	getPublishedBlogBySlug,
} from "../../services/blogService";
import "./NewsPage.css";

const formatDate = (value) => {
	if (!value) {
		return "";
	}
	return new Date(value).toLocaleDateString(undefined, {
		year: "numeric",
		month: "long",
		day: "numeric",
	});
};

const getCoverPosition = (blog) => {
	const x =
		typeof blog?.coverImage?.focalX === "number" ? blog.coverImage.focalX : 50;
	const y =
		typeof blog?.coverImage?.focalY === "number" ? blog.coverImage.focalY : 50;
	return `${x}% ${y}%`;
};

const NewsArticlePage = () => {
	const { slug } = useParams();
	const dispatch = useAppDispatch();
	const { isLoggedIn, user } = useAppSelector((state) => state.user);
	const [blog, setBlog] = useState(null);
	const [loading, setLoading] = useState(true);
	const [notFound, setNotFound] = useState(false);
	const [comments, setComments] = useState([]);
	const [commentsTotal, setCommentsTotal] = useState(0);
	const [commentsLoading, setCommentsLoading] = useState(false);
	const [commentDraft, setCommentDraft] = useState("");
	const [commentSubmitting, setCommentSubmitting] = useState(false);
	const [commentError, setCommentError] = useState("");

	const loadComments = async (targetSlug) => {
		if (!targetSlug) {
			return;
		}
		try {
			setCommentsLoading(true);
			setCommentError("");
			const response = await getBlogComments(targetSlug, { page: 1, limit: 100 });
			setComments(response.data?.items || []);
			setCommentsTotal(response.data?.total || 0);
		} catch (error) {
			console.error("Failed to load blog comments:", error);
			setCommentError("Unable to load comments right now.");
			setComments([]);
			setCommentsTotal(0);
		} finally {
			setCommentsLoading(false);
		}
	};

	useEffect(() => {
		if (!slug) {
			return;
		}

		const fetchBlog = async () => {
			try {
				setLoading(true);
				setNotFound(false);
				const response = await getPublishedBlogBySlug(slug);
				setBlog(response.data);
				await loadComments(slug);
			} catch (error) {
				if (error?.response?.status === 404) {
					setNotFound(true);
				}
				console.error("Failed to load blog article:", error);
				setBlog(null);
				setComments([]);
				setCommentsTotal(0);
			} finally {
				setLoading(false);
			}
		};

		void fetchBlog();
	}, [slug]);

	if (loading) {
		return <Container className="news-article">Loading article...</Container>;
	}

	if (notFound || !blog) {
		return (
			<Container className="news-article">
				<div className="news-empty">
					Article not found. <Link to="/news">Back to News</Link>
				</div>
			</Container>
		);
	}

	const openLoginModal = () => {
		dispatch(setShowLoginModal(true));
	};

	const handleSubmitComment = async (event) => {
		event.preventDefault();
		if (!slug) {
			return;
		}
		if (!isLoggedIn) {
			openLoginModal();
			return;
		}

		const nextComment = commentDraft.trim();
		if (!nextComment) {
			return;
		}

		try {
			setCommentSubmitting(true);
			setCommentError("");
			const response = await createBlogComment(slug, nextComment);
			setComments((prev) => [response.data, ...prev]);
			setCommentsTotal((prev) => prev + 1);
			setCommentDraft("");
		} catch (error) {
			if (error?.response?.status === 401) {
				openLoginModal();
				return;
			}
			const message =
				typeof error?.response?.data === "string"
					? error.response.data
					: "Failed to post comment.";
			setCommentError(message);
		} finally {
			setCommentSubmitting(false);
		}
	};

	const handleDeleteComment = async (commentId) => {
		if (!commentId || !window.confirm("Delete this comment?")) {
			return;
		}
		try {
			await deleteBlogComment(commentId);
			setComments((prev) => prev.filter((item) => item._id !== commentId));
			setCommentsTotal((prev) => Math.max(0, prev - 1));
		} catch (error) {
			console.error("Failed to delete comment:", error);
			alert("Failed to delete comment.");
		}
	};

	const canDeleteComment = (comment) => {
		const commentAuthorId = comment?.author?._id;
		const currentUserId = user?._id;
		return Boolean(
			isLoggedIn &&
				currentUserId &&
				(commentAuthorId === currentUserId || user?.role === "admin"),
		);
	};

	return (
		<Container className="news-article">
			<Helmet>
				<title>{blog.seo?.metaTitle || blog.title} - The Race Companion</title>
				<meta
					name="description"
					content={blog.seo?.metaDescription || blog.excerpt || ""}
				/>
			</Helmet>

			<div className="mb-3">
				<Link to="/news">&larr; Back to News</Link>
			</div>

			<article className="news-article-wrap">
				{blog?.coverImage?.url ? (
					<img
						src={blog.coverImage.url}
						alt={blog.title}
						className="news-article-cover"
						style={{ objectPosition: getCoverPosition(blog) }}
					/>
				) : null}
				<div className="news-article-body">
					<div className="news-date">{formatDate(blog.publishedAt)}</div>
					<h1 className="news-article-title">{blog.title}</h1>
					<div
						className="news-article-content"
						dangerouslySetInnerHTML={{ __html: blog.contentHtml || "" }}
					/>
				</div>
			</article>

			<section className="news-comments">
				<div className="news-comments-header">
					<h2 className="news-comments-title">Comments ({commentsTotal})</h2>
				</div>

				{isLoggedIn ? (
					<Form className="news-comment-form" onSubmit={handleSubmitComment}>
						<Form.Group controlId="newsCommentDraft">
							<Form.Control
								as="textarea"
								rows={3}
								maxLength={1000}
								value={commentDraft}
								onChange={(event) => setCommentDraft(event.target.value)}
								placeholder="Share your thoughts..."
							/>
						</Form.Group>
						<div className="news-comment-form-footer">
							<div className="news-comment-count">
								{commentDraft.length}/1000
							</div>
							<Button
								type="submit"
								variant="primary"
								disabled={commentSubmitting || !commentDraft.trim()}
							>
								{commentSubmitting ? "Posting..." : "Post Comment"}
							</Button>
						</div>
					</Form>
				) : (
					<div className="news-comments-login-prompt">
						<Button variant="link" className="p-0" onClick={openLoginModal}>
							Log in
						</Button>{" "}
						to post a comment.
					</div>
				)}

				{commentError ? <div className="news-comment-error">{commentError}</div> : null}

				{commentsLoading ? <div className="news-comments-loading">Loading comments...</div> : null}

				{!commentsLoading && comments.length === 0 ? (
					<div className="news-comments-empty">
						No comments yet. Be the first to start the conversation.
					</div>
				) : null}

				<ul className="news-comments-list">
					{comments.map((comment) => (
						<li key={comment._id} className="news-comment-item">
							<div className="news-comment-meta">
								<span className="news-comment-author">
									{comment?.author?.username || "User"}
								</span>
								<span className="news-comment-date">
									{formatDate(comment.createdAt)}
								</span>
								{canDeleteComment(comment) ? (
									<Button
										variant="link"
										className="news-comment-delete"
										onClick={() => handleDeleteComment(comment._id)}
									>
										Delete
									</Button>
								) : null}
							</div>
							<p className="news-comment-content">{comment.content}</p>
						</li>
					))}
				</ul>
			</section>
		</Container>
	);
};

export default NewsArticlePage;
